import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateUserLanguage, getUserPreferredLanguage } from '../ducks/user.duck';

// Langues supportées
const SUPPORTED_LOCALES = ['fr', 'nl', 'en'];
const DEFAULT_LOCALE = 'fr';
const LOCALSTORAGE_KEY = 'syndemiolocale';

/**
 * Hook pour gérer la langue de l'app avec approche HYBRIDE
 * Ordre de priorité :
 * 1. Langue du profil utilisateur (si connecté)
 * 2. Langue du localStorage (si non connecté)
 * 3. Langue du navigateur (détection auto)
 * 4. Français (défaut)
 */
export const useLocale = () => {
  const dispatch = useDispatch();

  // Récupère l'utilisateur actuel depuis Redux
  const currentUser = useSelector(state => state.user.currentUser);
  const isAuthenticated = !!currentUser?.id;

  /**
   * Fonction pour obtenir la locale initiale selon l'ordre de priorité
   */
  const getInitialLocale = () => {
    // 1️⃣ PRIORITÉ 1 : Langue du profil utilisateur (si connecté)
    if (isAuthenticated) {
      const userLang = getUserPreferredLanguage(currentUser);
      if (userLang && SUPPORTED_LOCALES.includes(userLang)) {
        console.log('🔵 Locale depuis profil utilisateur:', userLang);
        // Synchronise avec localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem(LOCALSTORAGE_KEY, userLang);
        }
        return userLang;
      }
    }

    // 2️⃣ PRIORITÉ 2 : Langue du localStorage
    const storedLocale = typeof window !== 'undefined' ? localStorage.getItem(LOCALSTORAGE_KEY) : null;
    if (storedLocale && SUPPORTED_LOCALES.includes(storedLocale)) {
      console.log('🟡 Locale depuis localStorage:', storedLocale);
      return storedLocale;
    }

    // 3️⃣ PRIORITÉ 3 : Langue du navigateur
    if (typeof window !== 'undefined' && navigator) {
      const browserLang = (navigator.language || navigator.userLanguage).split('-')[0];
      if (SUPPORTED_LOCALES.includes(browserLang)) {
        console.log('🟢 Locale détectée navigateur:', browserLang);
        localStorage.setItem(LOCALSTORAGE_KEY, browserLang);
        return browserLang;
      }
    }

    // 4️⃣ PRIORITÉ 4 : Fallback français
    console.log('⚪ Locale par défaut:', DEFAULT_LOCALE);
    if (typeof window !== 'undefined') {
      localStorage.setItem(LOCALSTORAGE_KEY, DEFAULT_LOCALE);
    }
    return DEFAULT_LOCALE;
  };

  const [locale, setLocaleState] = useState(getInitialLocale);

  /**
   * Fonction pour changer la locale
   * Sauvegarde dans localStorage + profil utilisateur (si connecté)
   */
  const setLocale = newLocale => {
    if (!SUPPORTED_LOCALES.includes(newLocale)) {
      console.error('❌ Locale non supportée:', newLocale);
      return;
    }

    console.log('🔄 Changement de locale:', locale, '→', newLocale);

    // 1. Sauvegarde dans localStorage (immédiat)
    localStorage.setItem(LOCALSTORAGE_KEY, newLocale);

    // 2. Sauvegarde dans profil utilisateur (si connecté)
    if (isAuthenticated) {
      dispatch(updateUserLanguage(newLocale))
        .unwrap()
        .then(() => {
          console.log('✅ Langue synchronisée avec le profil');
        })
        .catch(e => {
          console.warn('⚠️ Impossible de sauvegarder dans le profil, localStorage utilisé', e);
        });
    }

    // 3. Met à jour le state local
    setLocaleState(newLocale);

    // 4. Recharge la page pour appliquer la nouvelle langue
    window.location.reload();
  };

  /**
   * Synchronisation automatique au changement d'utilisateur
   * Si un utilisateur se connecte, on charge sa langue préférée
   */
  useEffect(() => {
    if (isAuthenticated) {
      const userLang = getUserPreferredLanguage(currentUser);
      if (userLang && userLang !== locale && SUPPORTED_LOCALES.includes(userLang)) {
        console.log('🔁 Synchronisation langue après connexion:', userLang);
        setLocaleState(userLang);
        localStorage.setItem(LOCALSTORAGE_KEY, userLang);
        window.location.reload();
      }
    }
  }, [currentUser?.id]); // Se déclenche uniquement au changement d'ID utilisateur

  return {
    locale,
    setLocale,
    supportedLocales: SUPPORTED_LOCALES,
    isAuthenticated,
  };
};
